import { useMemo } from 'react'

import { useIntl } from 'react-intl'

import { getPaginationSlots } from '@b-stock/fe-core/bstock-react'

import { ArrowButton, ArrowLabel, Container, Ellipsis, PageButton } from './ui'

const MAX_SLOTS = 5

type Props = {
  isFetching?: boolean
  page: number
  perPage: number
  setPage: (page: number) => void
  total: number
}

export const Pagination = ({
  isFetching = false,
  page,
  perPage,
  setPage,
  total,
}: Props) => {
  const { formatMessage } = useIntl()

  const totalPages = Math.max(1, Math.ceil(total / perPage))
  const pages = useMemo(
    () =>
      getPaginationSlots({
        currentPage: page,
        pageCount: totalPages,
        maxSlots: MAX_SLOTS,
      }),
    [page, totalPages]
  )

  const isAtFirstPage = page <= 1
  const isAtLastPage = page >= totalPages

  const previousLabel = formatMessage({ id: 'Common.pagination.previous' })
  const nextLabel = formatMessage({ id: 'Common.pagination.next' })

  return (
    <Container>
      {!isAtFirstPage && (
        <ArrowButton
          aria-label={previousLabel}
          disabled={isFetching}
          onClick={() => setPage(page - 1)}
          type="button"
        >
          ←<ArrowLabel>{previousLabel}</ArrowLabel>
        </ArrowButton>
      )}
      {pages.map((value) => {
        if (value < 0) {
          return (
            <Ellipsis aria-hidden key={value}>
              …
            </Ellipsis>
          )
        }
        const isCurrent = value === page
        return (
          <PageButton
            $current={isCurrent}
            aria-current={isCurrent ? 'page' : undefined}
            disabled={isFetching}
            key={value}
            onClick={() => setPage(value)}
            type="button"
          >
            {value}
          </PageButton>
        )
      })}
      {!isAtLastPage && (
        <ArrowButton
          aria-label={nextLabel}
          disabled={isFetching}
          onClick={() => setPage(page + 1)}
          type="button"
        >
          <ArrowLabel>{nextLabel}</ArrowLabel>→
        </ArrowButton>
      )}
    </Container>
  )
}
